import { useQuery } from 'react-query'
import { feedbackAPI } from '../../lib/api'
import { motion } from 'framer-motion'
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'
import { TrendingUp, TrendingDown, Users, MessageSquare, Search, AlertCircle } from 'lucide-react'

export function Statistics() {
  const { data: stats, isLoading } = useQuery(
    'feedback-stats',
    () => feedbackAPI.getStats()
  )

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  const knowledgeGapData = stats ? [
    { name: 'Offen', value: stats.knowledge_gaps.open, color: '#EAB308' },
    { name: 'In Bearbeitung', value: stats.knowledge_gaps.in_progress, color: '#3B82F6' },
    { name: 'Gelöst', value: stats.knowledge_gaps.resolved, color: '#10B981' },
  ] : []

  const searchFeedbackData = stats ? [
    { name: 'Positiv', value: stats.search_feedback.positive, color: '#10B981' },
    { name: 'Neutral', value: stats.search_feedback.neutral, color: '#6B7280' },
    { name: 'Negativ', value: stats.search_feedback.negative, color: '#EF4444' },
  ] : []

  // Mock data for demonstration
  const usageData = [
    { date: 'Mo', chats: 45, searches: 120 },
    { date: 'Di', chats: 52, searches: 98 },
    { date: 'Mi', chats: 38, searches: 156 },
    { date: 'Do', chats: 65, searches: 142 },
    { date: 'Fr', chats: 48, searches: 178 },
    { date: 'Sa', chats: 23, searches: 67 },
    { date: 'So', chats: 18, searches: 45 },
  ]

  const statCards = [
    {
      title: 'Aktive Nutzer',
      value: stats?.usage_stats?.active_users || 0,
      change: stats?.usage_stats?.trends?.users_change || '+0%',
      trend: stats?.usage_stats?.trends?.users_change?.startsWith('+') ? 'up' : 
             stats?.usage_stats?.trends?.users_change?.startsWith('-') ? 'down' : 'neutral',
      icon: Users,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: 'Chat-Anfragen',
      value: stats?.usage_stats?.chat_requests || 0,
      change: stats?.usage_stats?.trends?.chats_change || '+0%',
      trend: stats?.usage_stats?.trends?.chats_change?.startsWith('+') ? 'up' : 
             stats?.usage_stats?.trends?.chats_change?.startsWith('-') ? 'down' : 'neutral',
      icon: MessageSquare,
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: 'Suchanfragen',
      value: stats?.usage_stats?.search_requests || 0,
      change: stats?.usage_stats?.trends?.searches_change || '+0%',
      trend: stats?.usage_stats?.trends?.searches_change?.startsWith('+') ? 'up' : 
             stats?.usage_stats?.trends?.searches_change?.startsWith('-') ? 'down' : 'neutral',
      icon: Search,
      color: 'from-green-500 to-green-600'
    },
    {
      title: 'Offene Fragen',
      value: stats?.knowledge_gaps.open || 0,
      change: '0',
      trend: 'neutral',
      icon: AlertCircle,
      color: 'from-yellow-500 to-yellow-600'
    },
  ]

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="glass rounded-xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex items-center space-x-1">
                  {stat.trend === 'up' && <TrendingUp className="w-4 h-4 text-green-500" />}
                  {stat.trend === 'down' && <TrendingDown className="w-4 h-4 text-red-500" />}
                  <span className={`text-sm ${
                    stat.trend === 'up' ? 'text-green-500' : 
                    stat.trend === 'down' ? 'text-red-500' : 
                    'text-gray-500'
                  }`}>
                    {stat.change}
                  </span>
                </div>
              </div>
              <h3 className="text-2xl font-bold">{stat.value}</h3>
              <p className="text-sm text-gray-400">{stat.title}</p>
            </motion.div>
          )
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage Chart */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="glass rounded-xl p-6"
        >
          <h3 className="text-lg font-bold mb-4">Nutzung diese Woche</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={usageData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(17, 24, 39, 0.9)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="chats"
                stroke="#3B82F6"
                strokeWidth={2}
                name="Chats"
              />
              <Line
                type="monotone"
                dataKey="searches"
                stroke="#10B981"
                strokeWidth={2}
                name="Suchen"
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Knowledge Gaps Pie Chart */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="glass rounded-xl p-6"
        >
          <h3 className="text-lg font-bold mb-4">Wissenslücken Status</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={knowledgeGapData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {knowledgeGapData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(17, 24, 39, 0.9)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Search Feedback Chart */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
          className="glass rounded-xl p-6"
        >
          <h3 className="text-lg font-bold mb-4">Such-Feedback</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={searchFeedbackData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="name" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(17, 24, 39, 0.9)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                }}
              />
              <Bar dataKey="value" fill="#8884d8">
                {searchFeedbackData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Popular Searches */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5 }}
          className="glass rounded-xl p-6"
        >
          <h3 className="text-lg font-bold mb-4">Häufigste Suchanfragen</h3>
          <div className="space-y-3">
            {[
              { query: 'Pflegegrade Antrag', count: 234 },
              { query: 'Abrechnungsformulare', count: 189 },
              { query: 'Schulungsunterlagen', count: 156 },
              { query: 'Qualitätsmanagement', count: 134 },
              { query: 'Mitarbeiterführung', count: 98 },
            ].map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm">{item.query}</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-primary-500 h-2 rounded-full"
                      style={{ width: `${(item.count / 234) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 w-10 text-right">{item.count}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  )
}